import { Box, Typography, Link } from '@mui/material';
import useAssetronFooterStyles from '@assetron/web-common/components/common/styles/useAssetronFooterStyles';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocalization } from '@assetron/common/hooks/useLocalization';
import validateEmail from '@assetron/common/utils/validations/validateEmail';
import validateName from '@assetron/common/utils/validations/validateName';
import useSignupData from '@assetron/common/hooks/useSignupData';
import {
  DEBOUNCE_DELAY,
  EMPTY_STRING,
  INVITE_DETAILS,
  WHITESPACE,
} from '@assetron/common/constants/constant';
import { ResponseStatusCode } from '@assetron/common/types/responseStatus';
import { initAuth } from '@assetron/common/api';
import useDebounce from '@assetron/web-common/hooks/useDebounce';
import { useNavigation } from '../../navigation/useNavigation';
import useSignUpDetailsStyles from './styles/useSignupDetailsFormStyles';
import AssetronButton from '@assetron/web-common/components/common/AssetronButton';
import AssetronHeading from '@assetron/web-common/components/common/AssetronHeading';
import ErrorMessage from '@assetron/web-common/components/common/ErrorMessage';
import AuthAssetronInputField from '../common/AuthAssetronInputField';
import { createEnterKeyHandler } from '@assetron/common/utils/keyboardHandler';
import apiErrorHandler from '@assetron/common/utils/apiErrorHandler';
import { GA_EVENTS } from '@assetron/common/constants/googleAnalyticsEvents';
import { trackGaEvent } from '@assetron/common/utils/googleAnalytics';
import { billingFlowLog } from '@assetron/web-common/utils/billingFlowDiagnostics';

export default function SignupDetailsForm() {
  const { t } = useLocalization();

  const { userName, userEmail, setUserEmail, setUserName } = useSignupData();

  const { primaryFooterLinkStyle, privacyPolicyLink } =
    useAssetronFooterStyles();

  const [doesEmailExist, setDoesEmailExist] = useState(false);
  const [isEmailPrefilled, setIsEmailPrefilled] = useState(false);
  const [emailValue, setEmailValue] = useState(userEmail);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);

  const {
    formBox,
    formHeadingBox,
    subheading,
    form,
    inputContainerBox,
    button,
    nextButtonText,
    gdprText,
  } = useSignUpDetailsStyles();

  const { navigateToSignInLanding, navigateToSignUpEmailVerification } =
    useNavigation();

  useEffect(() => {
    const inviteDetails = sessionStorage.getItem(INVITE_DETAILS);
    if (inviteDetails) {
      const { name, email } = JSON.parse(inviteDetails);
      if (name) setUserName(name);
      if (email) {
        setUserEmail(email);
        setIsEmailPrefilled(true);
      }
    }
  }, [setUserName, setUserEmail]);

  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Name is required')
      .test('is-valid-name', 'Invalid name', validateName),
    email: Yup.string()
      .required('Email is required')
      .test('is-valid-email', 'Invalid email', validateEmail),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      name: userName,
      email: userEmail,
    },
  });

  useEffect(() => {
    const inviteDetails = sessionStorage.getItem(INVITE_DETAILS);
    if (inviteDetails) {
      const { name, email } = JSON.parse(inviteDetails);

      if (name) setUserName(name);
      if (email) {
        setUserEmail(email);
        setIsEmailPrefilled(true);
      }

      reset({
        name: name ?? EMPTY_STRING,
        email: email ?? EMPTY_STRING,
      });
    }
  }, [reset, setUserEmail, setUserName]);

  const initAuthMutation = useMutation({
    mutationFn: initAuth,
    onSuccess: () => {
      billingFlowLog('SignupDetails', 'signup_details_success', {
        email: userEmail,
        isEmailPrefilled,
      });
      trackGaEvent(GA_EVENTS.AUTH_SIGNUP_DETAILS_SUCCESS, {
        is_email_prefilled: isEmailPrefilled,
      });
      navigateToSignUpEmailVerification();
    },
    onError: (error) => {
      billingFlowLog('SignupDetails', 'signup_details_failure', {
        email: userEmail,
        status: error?.response?.status,
      });
      trackGaEvent(GA_EVENTS.AUTH_SIGNUP_DETAILS_FAILURE, {
        status_code: error?.response?.status,
      });
      if (error?.response?.status === ResponseStatusCode.Conflict) {
        setDoesEmailExist(true);
      } else {
        apiErrorHandler(error, {}, t('apiErrorText'));
      }
    },
  });

  const onSubmit = async (values: { name: string; email: string }) => {
    billingFlowLog('SignupDetails', 'signup_details_submit', {
      email: values.email,
      namePresent: Boolean(values.name),
      isEmailPrefilled,
    });
    trackGaEvent(GA_EVENTS.AUTH_SIGNUP_DETAILS_SUBMIT, {
      is_email_prefilled: isEmailPrefilled,
    });
    const data = {
      email: values.email,
      name: values.name,
    };
    setUserName(data.name);
    setUserEmail(data.email);
    initAuthMutation.mutate({ email: values.email });
  };
  const getErrorMessage = () => {
    if (doesEmailExist) {
      return (
        <ErrorMessage
          message={t('signUpDetailsPage.emailAlreadyExists')}
          isHavingPipe
          onLinkClick={() => {
            setUserName(EMPTY_STRING);
            setUserEmail(EMPTY_STRING);
            navigateToSignInLanding();
          }}
          linkText={t('signUpDetailsPage.signin')}
        />
      );
    } else if (isEmailInvalid) {
      return (
        <ErrorMessage message={t('signUpDetailsPage.invalidEmailError')} />
      );
    }
    return EMPTY_STRING;
  };
  const isFormValid =
    !errors.name &&
    watch('name') &&
    !errors.email &&
    watch('email') &&
    !isEmailInvalid &&
    !doesEmailExist;

  const debouncedEmail = useDebounce(emailValue, DEBOUNCE_DELAY);

  // Validate email format after debounce
  useEffect(() => {
    if (debouncedEmail) {
      const isValid = validateEmail(debouncedEmail);
      setIsEmailInvalid(!isValid);
    } else {
      setIsEmailInvalid(false);
    }
  }, [debouncedEmail]);

  return (
    <Box sx={formBox}>
      <Box sx={formHeadingBox}>
        <AssetronHeading title={t('signUpDetailsPage.createAccount')} />
        <Typography variant="body_14_regular" sx={subheading}>
          {t('signUpDetailsPage.addDetails')}
        </Typography>
      </Box>
      <Box
        sx={form}
        component="form"
        onKeyDown={createEnterKeyHandler({
          isEnabled: Boolean(isFormValid && !doesEmailExist),
          isLoading: initAuthMutation.isLoading,
          onEnter: () => handleSubmit(onSubmit)(),
        })}
      >
        <Box sx={inputContainerBox}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <AuthAssetronInputField
                label={t('signUpDetailsPage.nameLabel')}
                value={field.value}
                dataTestId="signup-name-input"
                onTextChange={(e) => {
                  const { value } = e.target;
                  field.onChange(
                    value.startsWith(WHITESPACE) ? value.trim() : value,
                  );
                }}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <AuthAssetronInputField
                label={t('signUpDetailsPage.emailLabel')}
                value={field.value}
                dataTestId="signup-email-input"
                destructive={doesEmailExist || isEmailInvalid}
                disabled={isEmailPrefilled}
                errorMessage={getErrorMessage()}
                onTextChange={(e) => {
                  if (doesEmailExist) {
                    setDoesEmailExist(false);
                  }
                  const { value } = e.target;
                  // Always trim to remove both leading and trailing whitespace
                  const formattedValue = value.trim().toLowerCase();
                  field.onChange(formattedValue);
                  setEmailValue(formattedValue);
                }}
              />
            )}
          />
        </Box>
        <Box sx={button}>
          <AssetronButton
            hierarchy="primary"
            disabled={!isFormValid || doesEmailExist}
            onClick={handleSubmit(onSubmit)}
            isLoading={initAuthMutation.isLoading}
            size="md"
            dataTestId="signup-details-next-button"
          >
            <Typography variant="body_14_medium" sx={nextButtonText}>
              {t('next')}
            </Typography>
          </AssetronButton>
          <Typography variant="body_12_regular" sx={gdprText}>
            {t('signUpDetailsPage.gdprText1')}
            <AssetronButton hierarchy="link-color">
              <Link
                href={privacyPolicyLink}
                target="_blank"
                component="a"
                sx={primaryFooterLinkStyle}
                variant="body_12_regular"
              >
                {t('footer.privacyPolicy')}
              </Link>
            </AssetronButton>
            {t('signUpDetailsPage.gdprText2')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
